import React, { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import ReactTanstackTableUi from "./ReactTanstackTableUi";
import { usePagination } from "../features/hooks/usePagination";
import { formatDateTimeAndYear } from "../utils/date-utils";
import { useNavigate } from "react-router-dom";

// --- helpers ---
// const fmtDur = (sec: number) => `${Math.floor(sec / 60)}m ${sec % 60}s`;
// const fmtDT = (d: Date | string | number) => new Date(d).toLocaleString();

export type Chat = {
  id: string;
  name: string;
  createdAt: Date | string | number;
  prompts: number;
  durationSec: number;
  status: "Completed" | "Active";
};

export type Project = {
  id: string;
  name: string;
  createdAt: Date | string | number;
};

export type AgentRun = {
  id: string;
  agent: string;
  at: Date | string | number;
};

export type Session = {
  id: string;
  start: Date | string | number;
  durationSec: number;
};

export type Feedback = {
  id: string;
  type: string;
  createdAt: Date | string | number;
};

// --- Columns ---
function prepareChatColumns(columnHelper: any, navigate?: any) {
  return [
    columnHelper.accessor((row: any) => row.chatListName, {
      id: "List Name",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;
        const fetchId = row?.original?.id;

        return (
          <div
            className="flex flex-col gap-1 underline cursor-pointer"
            onClick={() => navigate(`/chat/${fetchId}`)}
          >
            <div className="font-semibold text-gray-800 text-sm truncate w-32 max-w-xs ">
              {original?.chatListName || "--"}
            </div>
          </div>
        );
      },
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor((row: any) => row.createdat, {
      id: "Created At",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;

        return (
          <div className="flex flex-col gap-1">
            <div className="font-semibold text-gray-800 text-sm max-w-xs">
              {original?.timestamp
                ? (() => {
                    const { date, time } = formatDateTimeAndYear(
                      original.timestamp
                    );
                    return `${date} ${time}`;
                  })()
                : "--"}
            </div>
          </div>
        );
      },
      header: () => <span>Created At</span>,
    }),

    columnHelper.accessor((row: any) => row.length, {
      id: "length",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;
        return (
          <div className="flex flex-col gap-1">
            <div className="font-semibold text-gray-800 text-sm max-w-xs">
              {original?.length || "--"}
            </div>
          </div>
        );
      },
      header: () => <span>Total Prompts</span>,
    }),

    columnHelper.accessor((row: any) => row.duration, {
      id: "duration",
      cell: (info: any) => {
        const duration = info.getValue();

        if (!duration) return "--";

        const { hours, minutes, seconds } = duration;
        return (
          <div className="font-semibold text-gray-800 text-sm">
            {`${hours}h ${minutes}m ${seconds}s`}
          </div>
        );
      },
      header: () => <span>Chat Duration</span>,
    }),

    columnHelper.accessor((row: any) => row.createdBy, {
      id: "Created By",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;
        return (
          <div className="flex flex-col gap-1">
            <div className="font-semibold text-gray-800 text-sm max-w-xs">
              {original?.createdBy || "--"}
            </div>
          </div>
        );
      },
      header: () => <span>Created By</span>,
    }),

    columnHelper.accessor((row: any) => row.status, {
      id: "Status",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;
        return (
          <div className="flex flex-col gap-1">
            <div className="capitalize font-semibold text-gray-800 text-sm max-w-xs">
              {original?.status || "--"}
            </div>
          </div>
        );
      },
      header: () => <span>Status</span>,
    }),
  ];
}

function prepareProjectColumns(columnHelper: any) {
  return [
    columnHelper.accessor((row: any) => row.projectListName, {
      id: "projectListName",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;

        return (
          <div className=" flex flex-col gap-1">
            <div className="font-semibold text-gray-800 text-sm truncate w-full max-w-xs">
              {original?.activity || "--"}
            </div>
          </div>
        );
      },
      header: () => <div>Name</div>,
    }),
    columnHelper.accessor((row: any) => row.createdat, {
      id: "Created At",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;

        return (
          <div className=" flex flex-col gap-1">
            <div className="font-semibold text-gray-800 text-sm max-w-xs">
              {/* {original?.createdAt || "--"} */}
              {original?.createdAt
                ? (() => {
                    const { date, time } = formatDateTimeAndYear(
                      original.createdAt
                    );
                    return `${date} ${time}`;
                  })()
                : "--"}
            </div>
          </div>
        );
      },
      header: () => <div>Created At</div>,
    }),
    columnHelper.accessor((row: any) => row.createdBy, {
      id: "Created By",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;

        return (
          <div className=" flex flex-col gap-1">
            <div className="font-semibold text-gray-800 text-sm max-w-xs">
              {original?.createdBy || "--"}
            </div>
          </div>
        );
      },
      header: () => <div>Created By</div>,
    }),
    // columnHelper.accessor((row: any) => row.actions, {
    //   id: "Actions",
    //   cell: (info: any) => {
    //     const row = info.row;
    //     const original = row?.original;

    //     return (
    //       <div className="flex items-center space-x-4">
    //         {/* View Icon */}
    //         <FaEye
    //           className="text-blue-500 cursor-pointer"
    //           onClick={() => handleActions("view", original?.id)}
    //           title="View"
    //         />

    //         {/* Delete Icon */}
    //         <FaTrashAlt
    //           className="text-red-500 cursor-pointer"
    //           onClick={() => handleActions("delete", original?.id)}
    //           title="Delete"
    //         />
    //       </div>
    //     );
    //   },
    //   header: () => <div>Actions</div>,
    // }),
  ];
}

function prepareSessionColumns(columnHelper: any) {
  return [
    // columnHelper.accessor((row: any) => `${row.username} ${row.user_email}`, {
    //   id: "username",
    //   cell: (info: any) => {
    //     const original = info.row.original;
    //     console.log("original", original);

    //     return (
    //       <div className=" text-gray-800 text-base truncate space-y-1  ">
    //         <div className="font-medium text-gray-900 text-xs leading-5">
    //           {original?.username || "--"}
    //         </div>
    //         <div className=" w-32  text-xs text-gray-500  ">
    //           {original?.user_email || "--"}
    //         </div>
    //       </div>
    //     );
    //   },
    //   header: () => <div>Name</div>,
    // }),

    columnHelper.accessor((row: any) => row.country, {
      id: "country",
      cell: (info: any) => {
        const original = info?.row?.original;

        return (
          <div className="font-normal text-gray-900 text-sm whitespace-nowrap leading-5">
            {Array.isArray(original?.country) && original.country.length > 1 ? (
              original.country[1]
            ) : typeof original?.country === "string" ? (
              original.country
            ) : (
              <div className="flex justify-center items-center w-full">--</div>
            )}
          </div>
        );
      },
      header: () => <div>Location</div>,
    }),

    columnHelper.accessor((row: any) => row.loginTime, {
      id: "loginTime",
      cell: (info: any) => {
        const original = info?.row?.original;

        return (
          <div>
            {original?.login_time ? (
              (() => {
                const { date, time } = formatDateTimeAndYear(
                  original.login_time
                );
                return (
                  <div className=" text-gray-900 text-sm leading-5 ">
                    {`${date} ${time}`}
                  </div>
                );
              })()
            ) : (
              <div className="flex justify-center items-center ">--</div>
            )}
          </div>
        );
      },
      header: () => <div>Last Logged in </div>,
    }),

    columnHelper.accessor((row: any) => row.logoutTime, {
      id: "logoutTime",
      cell: (info: any) => {
        const original = info?.row?.original;

        return (
          <div>
            {original?.logout_time ? (
              (() => {
                const { date, time } = formatDateTimeAndYear(
                  original.logout_time
                );
                return (
                  <div className="font-normal text-gray-900 text-sm leading-5 ">
                    {`${date} ${time}`}
                  </div>
                );
              })()
            ) : (
              <div className="font-normal text-gray-900 text-sm leading-5">
                --
              </div>
            )}
          </div>
        );
      },
      header: () => <div>Logout </div>,
    }),

    columnHelper.accessor((row: any) => row.totalClicks, {
      id: "totalClicks",
      cell: (info: any) => {
        const original = info?.row?.original;
        const userActivity = original?.user_activity || [];

        const totalClicks = userActivity.reduce(
          (acc: number, activity: any) => acc + (activity.total_hits || 0),
          0
        );

        return (
          <div className="font-normal text-gray-900 text-sm leading-5 text-center">
            {totalClicks}
          </div>
        );
      },
      header: () => <div className="text-center">User Engagement</div>,
    }),

    columnHelper.accessor((row: any) => row.device, {
      id: "device",
      cell: (info: any) => {
        const original = info?.row?.original;

        const isMobileDevice = () => {
          if (typeof window === "undefined") return "Unknown";
          const userAgent = navigator.userAgent || navigator.vendor;

          return /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent)
            ? "Mobile"
            : "Desktop";
        };

        return (
          <div className="flex flex-col gap-1">
            {original?.user_agent ? (
              <div className="font-normal text-gray-900 text-xs leading-5 max-w-xs">
                {isMobileDevice()}
              </div>
            ) : (
              <div className="font-normal text-gray-900 text-xs leading-5">
                --
              </div>
            )}
          </div>
        );
      },
      header: () => <div>Device</div>,
    }),

    columnHelper.accessor((row: any) => row.activeSession, {
      id: "Active Session",
      cell: (info: any) => {
        const original = info.row.original;

        return (
          <div className="flex flex-col gap-1">
            {original?.status ? (
              <div className="font-normal text-gray-900 text-xs leading-5 max-w-xs">
                {original?.status}
              </div>
            ) : (
              <div className="font-normal text-gray-900 text-xs leading-5">
                --
              </div>
            )}
          </div>
        );
      },
      header: () => <div>Status</div>,
    }),
  ];
}

function prepareFeedbackColumns(columnHelper: any, navigate?: any) {
  return [
    columnHelper.accessor((row: any) => row.username, {
      id: "username",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;
        const fetchId = row?.original?.id;

        return (
          <div
            className=" flex flex-col gap-1 underline cursor-pointer"
            onClick={() => navigate(`/feedback/${fetchId}`)}
          >
            <div className="font-semibold text-gray-800 text-sm truncate w-full max-w-xs">
              {original?.username || "--"}
            </div>
          </div>
        );
      },
      header: () => <div>Username</div>,
    }),
    columnHelper.accessor((row: any) => row.prompt_text, {
      id: "prompt_text",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;

        return (
          <div className=" flex flex-col gap-1">
            <div className="font-semibold text-gray-800 text-sm truncate w-full max-w-xs">
              {original?.prompt_text || "--"}
            </div>
          </div>
        );
      },
      header: () => <div>Message</div>,
    }),
    columnHelper.accessor((row: any) => row.llm, {
      id: "llm",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;

        return (
          <div className=" flex flex-col gap-1">
            <div className="font-semibold text-gray-800 text-sm max-w-xs">
              {original?.llm || "--"}
            </div>
          </div>
        );
      },
      header: () => <div>LLM</div>,
    }),
    columnHelper.accessor((row: any) => row.reaction, {
      id: "reaction",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;

        return (
          <div className=" flex flex-col gap-1">
            <div className="font-semibold text-gray-800 text-sm max-w-xs">
              {original?.reaction || "--"}
            </div>
          </div>
        );
      },
      header: () => <div>Reaction</div>,
    }),
    columnHelper.accessor((row: any) => row.custom_feedback, {
      id: "custom_feedback",
      cell: (info: any) => {
        const row = info.row;
        const original = row?.original;

        return (
          <div className=" flex flex-col gap-1">
            <div className="font-semibold text-gray-800 text-sm max-w-xs">
              {original?.custom_feedback ||
                original?.selected_feedback[0] ||
                "--"}
            </div>
          </div>
        );
      },
      header: () => <div>Reaction</div>,
    }),
    // columnHelper.accessor((row: any) => row.actions, {
    //   id: "Actions",
    //   cell: (info: any) => {
    //     const row = info.row;
    //     const original = row?.original;

    //     return (
    //       <div className="flex items-center space-x-4">
    //         {/* View Icon */}
    //         <FaEye
    //           className="text-blue-500 cursor-pointer"
    //           onClick={() =>
    //             // handleActions("view", original?.research_id)
    //             console.log("view", original?.id)
    //           }
    //           title="View"
    //         />

    //         {/* Delete Icon */}
    //         <FaTrashAlt
    //           className="text-red-500 cursor-pointer"
    //           onClick={() =>
    //             // handleActions("delete", original?.research_id)
    //             console.log("delete", original?.id)
    //           }
    //           title="Delete"
    //         />
    //       </div>
    //     );
    //   },
    //   header: () => <div>Actions</div>,
    // }),
  ];
}

// --- Wrapper ---
// type Kind = "chats" | "projects" | "agents" | "sessions" | "feedbacks";

type Props =
  | {
      kind: "chats";
      rows: Chat[];
      title?: string;
      loading?: boolean;
      data?: any;
    }
  | {
      kind: "projects";
      rows: Project[];
      title?: string;
      loading?: boolean;
      data?: any;
    }
  | {
      kind: "agents";
      rows: AgentRun[];
      title?: string;
      loading?: boolean;
      data?: any;
    }
  | {
      kind: "sessions";
      rows: Session[];
      title?: string;
      loading?: boolean;
      data?: any;
    }
  | {
      kind: "feedbacks";
      rows: Feedback[];
      title?: string;
      loading?: boolean;
      data?: any;
    };

const TabbedTanstackTable: React.FC<Props> = ({
  kind,
  rows,
  title,
  loading,
  data,
}) => {
  const { getPaginationInfo } = usePagination();

  const columnHelper = createColumnHelper();
  const navigate = useNavigate();

  const { columns, tableTitle, empty } = useMemo(() => {
    switch (kind) {
      case "chats":
        return {
          columns: prepareChatColumns(columnHelper, navigate),
          tableTitle: title ?? "Recent Chats",
          empty: "No chats available",
        };
      case "projects":
        return {
          columns: prepareProjectColumns(columnHelper),
          tableTitle: title ?? "Recent Projects",
          empty: "No projects available",
        };
      case "agents":
        return {
          columns: prepareChatColumns(columnHelper, navigate),
          tableTitle: title ?? "Agent Usage",
          empty: "No agent runs available",
        };
      case "sessions":
        return {
          columns: prepareSessionColumns(columnHelper),
          tableTitle: title ?? "User Sessions",
          empty: "No sessions available",
        };
      case "feedbacks":
        return {
          columns: prepareFeedbackColumns(columnHelper, navigate),
          tableTitle: title ?? "User Feedback",
          empty: "No feedback available",
        };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, title]);

  const paginationInfo = getPaginationInfo(data);

  return (
    <ReactTanstackTableUi
      data={rows as any[]}
      columns={columns}
      tableTitle={tableTitle}
      noDataMessage={empty}
      pagination={true}
      externalPagination={paginationInfo}
      disableRowHeight={true}
      isLoading={loading}
      individualUser={true}
    />
  );
};

export default TabbedTanstackTable;
